import { useCallback, useEffect, useState } from "react";
import { FolderSync, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CopyField } from "@/client/components/art/gpu-worker-dialog";
import { Button } from "@/client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import { formatRelativeTime } from "@/client/hooks/use-document";
import { apiJson, describeError } from "@/client/lib/api";
import { cn } from "@/client/lib/utils";

interface Helper {
  online: boolean;
  lastSeenAt: string | null;
}

/** While it's open, how often the helper's status is checked */
const REFRESH_MS = 10_000;

/** Keeping a story's scenes and codex entries in a folder of text files, through renteller (sync/) */
export function FolderSyncDialog({
  storyId,
  open,
  onOpenChange,
}: {
  storyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const path = `/api/sync-tokens/story/${storyId}`;
  const [helper, setHelper] = useState<Helper | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const result = await apiJson<{ helper: Helper | null }>(path);
    setHelper(result.helper);
  }, [path]);

  useEffect(() => {
    void refresh().catch(() => {});
    const timer = setInterval(() => void refresh().catch(() => {}), REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const newToken = async () => {
    if (helper && !window.confirm("The helper using the current token will stop syncing. Make a new one?")) return;
    setBusy(true);
    try {
      const result = await apiJson<{ token: string }>(path, { method: "POST" });
      setToken(result.token);
      await refresh();
    } catch (error) {
      toast.error(`Couldn't create a token: ${describeError(error)}`);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke the token? The helper stops syncing, and the files stay where they are.")) return;
    try {
      await apiJson(path, { method: "DELETE" });
      setToken(null);
      await refresh();
    } catch (error) {
      toast.error(`Couldn't revoke the token: ${describeError(error)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderSync className="h-5 w-5" />
            Sync with a folder
          </DialogTitle>
          <DialogDescription>
            Keep this story's scenes and codex entries as text files on your computer, so you or an agent can edit them
            directly, or add new ones. Changes on either side reach the other. Moving and deleting stay in the app for now.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {helper ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3">
              <div className="flex items-center gap-2 text-sm">
                <span className={cn("h-2.5 w-2.5 rounded-full", helper.online ? "bg-green-500" : "bg-muted-foreground/50")} />
                <span className="font-medium">{helper.online ? "Syncing" : "Not running"}</span>
                <span className="text-muted-foreground">
                  {helper.lastSeenAt ? `last seen ${formatRelativeTime(new Date(helper.lastSeenAt))}` : "never connected"}
                </span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={newToken} disabled={busy}>
                  <KeyRound />
                  New token
                </Button>
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={revoke}>
                  Revoke
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-dashed p-4 text-sm">
              <p>
                You need the <code>renteller</code> program on your computer. To build it, run{" "}
                <code>bun run build:cli</code> in a checkout of the Renteller repository, then put{" "}
                <code>dist/renteller</code> anywhere on your PATH.
              </p>
              <Button onClick={newToken} disabled={busy}>
                {busy ? <Loader2 className="animate-spin" /> : <KeyRound />}
                Create a sync token
              </Button>
            </div>
          )}

          {token && (
            <div className="space-y-3 rounded-lg border border-primary/40 bg-primary/5 p-3">
              <p className="text-sm">This token is shown once and only covers this story. Pick your folder and run:</p>
              <CopyField label="Connect a folder" value={`renteller link ${window.location.origin} ${token} ~/my-game`} />
              <CopyField label="Start syncing" value="renteller run ~/my-game" />
              <CopyField label="Optional: make it a playable Ren'Py game" value="renteller init ~/my-game" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
